import { supabase } from "../supabaseClient";

const DEFAULT_BUCKET = "product-images";

const getBucketName = (bucket = DEFAULT_BUCKET) => bucket || DEFAULT_BUCKET;

const refreshStorageSession = async () => {
  const { data: { session }, error: sessionError } = await supabase.auth.getSession();
  if (sessionError) throw sessionError;

  const expiresSoon = !session?.expires_at || session.expires_at <= Math.floor(Date.now() / 1000) + 60;
  if (session && expiresSoon) {
    const { data: refreshed, error: refreshError } = await supabase.auth.refreshSession();
    if (refreshError || !refreshed.session) {
      throw new Error(refreshError?.message || "Your session has expired. Please sign in again.");
    }
  }
};

export const ensureBucket = async (bucket = DEFAULT_BUCKET) => {
  const targetBucket = getBucketName(bucket);

  try {
    await supabase.storage.createBucket(targetBucket, { public: true });
    return true;
  } catch (err) {
    try {
      const { data: buckets, error } = await supabase.storage.listBuckets();
      if (!error) {
        const exists = Array.isArray(buckets) && buckets.some((item) => item.name === targetBucket);
        return exists;
      }
    } catch (e) {
      // ignore
    }

    return false;
  }
};

const uploadImageToBucket = async (file, bucket, prefix, onProgress = null) => {
  if (!file) throw new Error("No file provided for upload.");

  const safeName = String(file.name || "image").replace(/[^a-z0-9_.-]/gi, "_");
  const path = `${prefix}/${Date.now()}_${safeName}`;
  const targetBucket = getBucketName(bucket);

  await refreshStorageSession();

  let interval = null;
  if (typeof onProgress === "function") {
    let progress = 0;
    onProgress(0);
    interval = setInterval(() => {
      progress = Math.min(80, progress + Math.floor(Math.random() * 12) + 1);
      onProgress(progress);
    }, 300);
  }

  let { error } = await supabase.storage.from(targetBucket).upload(path, file, { upsert: true });

  if (error && /row-level security|jwt|unauthorized|forbidden/i.test(error.message || "")) {
    await supabase.auth.refreshSession();
    ({ error } = await supabase.storage.from(targetBucket).upload(path, file, { upsert: true }));
  }

  if (interval) {
    clearInterval(interval);
  }

  if (error) {
    throw error;
  }

  try {
    const publicResult = supabase.storage.from(targetBucket).getPublicUrl(path);
    const publicUrl = publicResult?.data?.publicUrl || publicResult?.publicURL || publicResult?.publicUrl || null;
    if (!publicUrl) {
      throw new Error("Unable to get public URL for uploaded image.");
    }
    if (typeof onProgress === "function") onProgress(100);
    return publicUrl;
  } catch (err) {
    throw err;
  }
};

export const uploadProductImage = async (file, onProgress = null) =>
  uploadImageToBucket(file, "product-images", "products", onProgress);

export const uploadBlogImage = async (file, onProgress = null) =>
  uploadImageToBucket(file, "blog-images", "blog", onProgress);

export const uploadCampaignImage = async (file, onProgress = null) =>
  uploadImageToBucket(file, "campaign-images", "campaigns", onProgress);
